import type { FieldProvenance } from '@clm/shared-types';

/**
 * Where one field's value came from — the CR-01 half that names the model.
 *
 * `FieldProvenanceSchema` is discriminated on `is_ai_derived`, so the model,
 * the provider and the `extraction_schema_version` are structurally present
 * whenever a model produced the value and structurally absent when one did not.
 * This component switches on that discriminant and renders both branches; there
 * is no third case to fall through to.
 *
 * The accessible name carries the schema version and the model, not just the
 * colour. A reviewer confirming a field is making an assertion about a specific
 * model output under a specific governed artefact version — that is the whole
 * content of the evidence, and it must not be conveyed by a purple background.
 *
 * That detail is a visually hidden `<span>` rather than an `aria-label`.
 * `aria-label` is only reliably exposed on an element that has a role, and
 * these chips are roleless generics: several screen readers drop the label
 * outright, which would silently take the model and the schema version back out
 * of the accessible name. Hidden text is announced in reading order with no
 * such dependency. `title` stays for the pointer tooltip.
 */
export function ProvenanceChip({ provenance }: { readonly provenance: FieldProvenance }) {
  if (provenance.is_ai_derived) {
    const name =
      `AI-derived by ${provenance.provider_id} model ${provenance.model}, ` +
      `extraction schema ${provenance.extraction_schema_version}, ` +
      `extracted ${provenance.extracted_at_utc}`;
    return (
      <span className="tag ai" title={name}>
        AI · {provenance.extraction_schema_version}
        <span className="vh"> — {name}</span>
      </span>
    );
  }

  const originLabel = ORIGIN_LABEL[provenance.origin];
  const by = provenance.entered_by_upn ?? 'the system';
  const name = `${originLabel}, entered by ${by}, recorded ${provenance.recorded_at_utc}`;
  return (
    <span className="tag acc" title={name}>
      {originLabel}
      <span className="vh"> — {name}</span>
    </span>
  );
}

/**
 * A typed Record over the origin enum, so a new origin in @clm/shared-types is
 * a compile error here rather than a blank chip.
 */
const ORIGIN_LABEL: Readonly<Record<'manual_entry' | 'intake_metadata' | 'system_derived', string>> =
  {
    manual_entry: 'Entered by a person',
    intake_metadata: 'Known at intake',
    system_derived: 'Derived by the system',
  };

/**
 * The full provenance readout, for the field detail panel.
 *
 * The chip is the glance; this is the evidence. Every field the contract makes
 * structurally required for the branch is shown — nothing is summarised away,
 * because an auditor asks for `extraction_schema_version` first.
 */
export function ProvenanceDetail({ provenance }: { readonly provenance: FieldProvenance }) {
  return (
    <dl className="prov">
      {provenance.is_ai_derived ? (
        <>
          <dt>Source</dt>
          <dd>AI-derived</dd>
          <dt>Extraction schema version</dt>
          <dd>{provenance.extraction_schema_version}</dd>
          <dt>Provider</dt>
          <dd>{provenance.provider_id}</dd>
          <dt>Model</dt>
          <dd>{provenance.model}</dd>
          <dt>Extracted</dt>
          <dd>{provenance.extracted_at_utc}</dd>
        </>
      ) : (
        <>
          <dt>Source</dt>
          <dd>{ORIGIN_LABEL[provenance.origin]}</dd>
          <dt>Origin</dt>
          <dd>{provenance.origin}</dd>
          <dt>Entered by</dt>
          <dd>{provenance.entered_by_upn ?? 'the system (no person entered this value)'}</dd>
          <dt>Recorded</dt>
          <dd>{provenance.recorded_at_utc}</dd>
        </>
      )}
    </dl>
  );
}
